import React, { useState, useEffect } from 'react';
import { ChevronRight, FileText, Download, Calendar } from 'lucide-react';
import { API_URL, fetchData } from '../helpers/api';
import FormatDate from '../utils/dateTools';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const VieAdministrative = () => {
    const [hiringAds, setHiringAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxImage, setLightboxImage] = useState(null);

    useEffect(() => {
        const fetchHiringAds = async () => {
            try {
                setLoading(true);
                const fetchedAds = await fetchData('/api/hiring-ads?populate=*');

                // Sort ads by publication date, most recent first
                const sortedAds = fetchedAds.sort((a, b) =>
                    new Date(b.publishedAt) - new Date(a.publishedAt)
                );

                setHiringAds(sortedAds);
            } catch (error) {
                console.error('Failed to fetch hiring ads:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHiringAds();
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-warning" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="vie-administrative">
            {/* Hero Section */}
            <section className="bg-dark text-white py-5">
                <div className="container py-5 text-center">
                    <h1 className="display-4 fw-bold mb-4">
                        VIE <span className="text-warning">ADMINISTRATIVE</span>
                    </h1>
                    <p className="fs-5 mx-auto" style={{ maxWidth: '800px' }}>
                        Offres d'emploi, avis et informations administratives du Mémorial ACTe
                    </p>
                </div>
            </section>

            {/* Job Listings Section */}
            <section className="py-5 bg-white">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            {/* <h2 className="h3 fw-bold mb-4">Offres d'Emploi Actuelles</h2> */}

                            {hiringAds.length === 0 ? (
                                <div className="alert alert-info" role="alert">
                                    Aucune annonce n'est disponible pour le moment.
                                </div>
                            ) : (
                                hiringAds.map((ad) => (
                                    <div
                                        key={ad.id}
                                        className="card mb-4 border-0 shadow-sm hover-shadow transition"
                                    >
                                        <div className="card-body p-4">
                                          <div className="row g-4">
                                            <div className={ad.image ? 'col-md-8' : 'col-12'}>
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <h3 className="h4 fw-bold mb-0">{ad.title}</h3>
                                                <div className="d-flex align-items-center text-muted small">
                                                    <Calendar size={16} className="me-2" />
                                                    <FormatDate
                                                        dateString={ad.publishedAt}
                                                        format="long"
                                                    />
                                                </div>
                                            </div>

                                            {/* Job Description */}
                                            <div className="text-muted mb-4">
                                                <TruncatableHtmlContent
                                                    html={ad.content}
                                                />
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="d-flex gap-3 flex-wrap">
                                                {(Array.isArray(ad.document) ? ad.document : ad.document ? [ad.document] : []).map((doc) => (
                                                    <a
                                                        key={doc.id}
                                                        href={API_URL + doc.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-outline-dark rounded-pill px-3 d-inline-flex align-items-center"
                                                    >
                                                        <FileText size={16} className="me-2" />
                                                        {doc.name || 'Télécharger le document'}
                                                        <Download size={16} className="ms-2" />
                                                    </a>
                                                ))}
                                            </div>
                                            </div>

                                            {/* Optional image: shown whole (no cropping), click to enlarge */}
                                            {ad.image && (
                                                <div className="col-md-4">
                                                    <button
                                                        type="button"
                                                        className="btn p-0 border-0 w-100"
                                                        onClick={() => setLightboxImage(ad.image)}
                                                        title="Agrandir l'image"
                                                    >
                                                        <img
                                                            src={API_URL + ad.image.url}
                                                            alt={ad.image.alternativeText || ad.title}
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '320px', objectFit: 'contain' }}
                                                        />
                                                    </button>
                                                </div>
                                            )}
                                          </div>
                                        </div>
                                    </div>
                                ))
                            )}

                            <Lightbox
                                open={!!lightboxImage}
                                close={() => setLightboxImage(null)}
                                slides={lightboxImage ? [{ src: API_URL + lightboxImage.url, alt: lightboxImage.alternativeText || '' }] : []}
                                carousel={{ finite: true }}
                                render={{ buttonPrev: () => null, buttonNext: () => null }}
                            />

                            {/* Contact Section */}
                            <div className="text-center mt-5">
                                <h3 className="h4 fw-bold mb-3">Vous ne trouvez pas votre bonheur ?</h3>
                                <p className="text-muted mb-4">
                                    Envoyez-nous votre candidature spontanée ou restez informé de nos prochaines opportunités.
                                </p>
                                <a
                                    href="mailto:rh@memorial-acte.fr"
                                    className="btn btn-dark btn-lg rounded-pill px-4 d-inline-flex align-items-center"
                                >
                                    Contactez notre service RH
                                    <ChevronRight size={20} className="ms-2" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default VieAdministrative;